import { createTheme } from "@vanilla-extract/css";
import { hslaWithAlpha } from "../utils/hslaWithAlpha";
import { primitiveTokens, semanticThemeTokens } from "./ThemeTokens.css";

const backgroundGradient = {
  1: primitiveTokens.colors.gradients.dark[1],
  2: primitiveTokens.colors.gradients.dark[2],
  3: primitiveTokens.colors.gradients.dark[3],
  4: primitiveTokens.colors.gradients.dark[4]
};

const button = {
  primary: {
    disabled: {
      backgroundColor: primitiveTokens.colors.neutral[800],
      textColor: primitiveTokens.colors.neutral[500]
    },
    focus: { backgroundColor: primitiveTokens.colors.purple[400] },
    hover: { backgroundColor: primitiveTokens.colors.purple[300] },
    default: { backgroundColor: primitiveTokens.colors.purple[400] },
    active: { backgroundColor: primitiveTokens.colors.purple[500] },
    textColor: primitiveTokens.colors.white[1000]
  },
  secondary: {
    default: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16),
      borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.24)
    },
    disabled: {
      backgroundColor: primitiveTokens.colors.neutral[900],
      textColor: primitiveTokens.colors.neutral[500],
      borderColor: primitiveTokens.colors.neutral[800]
    },
    focus: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16),
      boxShadow: primitiveTokens.colors.shadow.focusRing
    },
    hover: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.24)
    },
    active: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36)
    },
    loading: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36)
    },
    textColor: primitiveTokens.colors.white[1000]
  },
  tertiary: {
    disabled: {
      textColor: primitiveTokens.colors.neutral[500]
    },

    hover: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.24)
    },
    active: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36)
    },
    loading: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36)
    },
    textColor: primitiveTokens.colors.white[1000]
  }
};

const checkbox = {
  labelTextColor: primitiveTokens.colors.purple[50],
  unchecked: {
    backgroundColor: primitiveTokens.colors.purple[600],
    default: {
      borderColor: primitiveTokens.colors.purple[300]
    },
    focus: {
      borderColor: primitiveTokens.colors.purple[300],
      boxShadow: primitiveTokens.colors.shadow.focusRing
    },
    hover: {
      backgroundColor: primitiveTokens.colors.purple[500],
      borderColor: primitiveTokens.colors.purple[200]
    },
    active: {
      backgroundColor: primitiveTokens.colors.purple[400],
      borderColor: primitiveTokens.colors.purple[200]
    },
    disabled: {
      labelTextColor: primitiveTokens.colors.neutral[400],
      backgroundColor: primitiveTokens.colors.neutral[50],
      borderColor: primitiveTokens.colors.neutral[200]
    }
  },
  checked: {
    checkmarkColor: primitiveTokens.colors.purple[700],
    borderColor: "transparent",
    backgroundColor: primitiveTokens.colors.purple[100],
    focus: {
      borderColor: "transparent",
      boxShadow: primitiveTokens.colors.shadow.focusRing
    },
    hover: {
      borderColor: primitiveTokens.colors.purple[300]
    },
    active: {
      backgroundColor: primitiveTokens.colors.purple[300],
      borderColor: "transparent"
    },
    disabled: {
      backgroundColor: primitiveTokens.colors.neutral[400],
      borderColor: primitiveTokens.colors.neutral[200],
      checkmarkColor: primitiveTokens.colors.neutral[700],
      labelTextColor: primitiveTokens.colors.neutral[700]
    }
  }
};

const chip = {
  backgroundColor: primitiveTokens.colors.purple[800],
  textColor: primitiveTokens.colors.purple[100],
  borderColor: primitiveTokens.colors.purple[600],
  closeIconColor: primitiveTokens.colors.purple[100],
  hoverBorderColor: primitiveTokens.colors.purple[500],
  disabledBackgroundColor: primitiveTokens.colors.neutral[800],
  disabledTextColor: primitiveTokens.colors.neutral[500],
  disabledBorderColor: primitiveTokens.colors.neutral[700],
  disabledCloseIconColor: primitiveTokens.colors.neutral[500]
};

const color = {
  textColor: primitiveTokens.colors.white[1000],
  backgroundColor: {
    default: primitiveTokens.colors.neutral[900]
  }
};

const dataTable = {
  primaryColor: primitiveTokens.colors.purple[400],
  colorScheme: "dark",
  // Solid, not translucent. AG Grid backs a pinned column with
  // `linear-gradient(--ag-data-background-color, …)` — which derives from this
  // — and pinned columns are sticky cells sharing the row's scroll container,
  // so anything see-through here shows the middle columns sliding underneath.
  // This is `purple[900]` at 0.48 already composited over the page background,
  // so it renders identically to the alpha value it replaces.
  backgroundColor: "hsla(253, 15%, 14%, 1)",
  borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16),
  headerBackgroundColor: hslaWithAlpha(
    primitiveTokens.colors.purple[900],
    0.24
  ),
  row: {
    colorPalette: {
      1: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.5),
      2: hslaWithAlpha(primitiveTokens.colors.purple[600], 0.6),
      3: hslaWithAlpha(primitiveTokens.colors.purple[800], 0.4),
      4: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.45),
      5: hslaWithAlpha(primitiveTokens.colors.purple[600], 0.7),
      6: hslaWithAlpha(primitiveTokens.colors.purple[900], 0.55),
      7: hslaWithAlpha(primitiveTokens.colors.purple[800], 0.35),
      8: hslaWithAlpha(primitiveTokens.colors.purple[500], 0.65),
      9: hslaWithAlpha(primitiveTokens.colors.purple[500], 0.75),
      10: hslaWithAlpha(primitiveTokens.colors.purple[900], 0.5)
    },
    crossTableRowStyle: {
      match: primitiveTokens.colors.green[800],
      shift: primitiveTokens.colors.yellow[800]
    },

    borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.08),
    backgroundColorOdd: hslaWithAlpha(primitiveTokens.colors.purple[900], 0.4),

    selected: {
      backgroundColor: primitiveTokens.colors.purple[500]
    },
    hover: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.neutral[50], 0.15)
    }
  },
  dropdown: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[900], 0.94)
  },
  cell: {
    positive: primitiveTokens.colors.green[400],
    negative: primitiveTokens.colors.red[400],
    warning: primitiveTokens.colors.yellow[400],
    highlight: primitiveTokens.colors.blue[400]
  }
};
const dialog = {
  overlay: {
    backgroundColor: "hsla(253, 2%, 0%, 0.64)" // A deeper semi-transparent black overlay
  },
  content: {
    background: primitiveTokens.colors.purple[800],
    boxShadow: primitiveTokens.colors.shadow.sheet,
    textColor: primitiveTokens.colors.white[1000]
  },
  title: {
    fontSize: primitiveTokens.type.md,
    textColor: primitiveTokens.colors.white[1000]
  },
  description: {
    textColor: primitiveTokens.colors.neutral[400],
    fontSize: primitiveTokens.type.sm
  },
  iconButton: {
    backgroundColor: primitiveTokens.colors.neutral[700],
    textColor: primitiveTokens.colors.neutral[200]
  }
};

const toast = {
  default: {
    backgroundColor: primitiveTokens.colors.purple[700],
    borderColor: primitiveTokens.colors.neutral[500],
    titleColor: primitiveTokens.colors.white[1000],
    descriptionColor: primitiveTokens.colors.neutral[300]
  },
  success: {
    backgroundColor: primitiveTokens.colors.green[700],
    borderColor: primitiveTokens.colors.green[500],
    titleColor: primitiveTokens.colors.white[1000],
    descriptionColor: primitiveTokens.colors.neutral[200]
  },
  warning: {
    backgroundColor: primitiveTokens.colors.yellow[700],
    borderColor: primitiveTokens.colors.yellow[500],
    titleColor: primitiveTokens.colors.white[1000],
    descriptionColor: primitiveTokens.colors.neutral[200]
  },
  error: {
    backgroundColor: primitiveTokens.colors.red[700],
    borderColor: primitiveTokens.colors.red[500],
    titleColor: primitiveTokens.colors.white[1000],
    descriptionColor: primitiveTokens.colors.neutral[200]
  }
};

const divider = {
  backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36),
  color: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36)
};

// Off-scale: the drawer wants a tint, and no purple step is that desaturated.
const drawer = {
  backgroundColor: "hsla(253, 20%, 21%, 1)"
};

const dropdown = {
  trigger: {
    default: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.04),
      color: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.64)
    },
    hover: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.12),
      color: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.8)
    },
    active: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.24),
      color: primitiveTokens.colors.white[1000]
    }
  },

  content: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.24),
    color: primitiveTokens.colors.white[1000]
  },
  item: {
    default: {
      backgroundColor: primitiveTokens.colors.white[100]
    },
    hover: {
      backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16),
      color: primitiveTokens.colors.white[1000]
    }
  }
};

const input = {
  backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[900], 0.48),
  cursorColor: primitiveTokens.colors.purple[200],
  filled: {
    active: {
      borderColor: primitiveTokens.colors.purple[300]
    },
    disabled: {
      backgroundColor: primitiveTokens.colors.neutral[800],
      borderColor: primitiveTokens.colors.neutral[700],
      textColor: primitiveTokens.colors.neutral[500]
    },
    hover: {
      borderColor: primitiveTokens.colors.purple[400]
    },
    default: {
      borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16)
    },
    textColor: primitiveTokens.colors.white[1000]
  },
  invalid: {
    backgroundColor: primitiveTokens.colors.red[900],
    borderColor: primitiveTokens.colors.red[400]
  },

  unfilled: {
    active: {
      borderColor: primitiveTokens.colors.purple[300]
    },
    disabled: {
      backgroundColor: primitiveTokens.colors.neutral[800],
      borderColor: primitiveTokens.colors.neutral[700],
      textColor: primitiveTokens.colors.neutral[600]
    },
    hover: {
      borderColor: primitiveTokens.colors.purple[400]
    },
    default: {
      borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16)
    },
    textColor: primitiveTokens.colors.neutral[500]
  }
};

const label = {
  color: {
    disabled: primitiveTokens.colors.neutral[600],
    optionalDisabled: primitiveTokens.colors.neutral[600],
    optionalRegular: primitiveTokens.colors.neutral[400],
    regular: primitiveTokens.colors.neutral[400]
  }
};

const loginPage = {
  background: primitiveTokens.colors.gradients.dark[5],
  heading: {
    color: primitiveTokens.colors.white[1000]
  },
  container: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16),
    borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16)
  },
  forgotPassword: {
    color: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.8)
  },
  copyRight: {
    color: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.48)
  }
};

const logo = { fill: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.8) };

const tile = {
  backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[900], 0.48),
  borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16)
};

const navigationMenu = {
  fontSize: primitiveTokens.type.sm,
  placeholderColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.64),
  selected: {
    color: primitiveTokens.colors.white[1000],
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36)
  }
};

const radio = {
  backgroundColor: primitiveTokens.colors.purple[600],
  borderColor: primitiveTokens.colors.purple[300],
  hover: {
    borderColor: primitiveTokens.colors.purple[200]
  },
  focus: {
    boxShadow: primitiveTokens.colors.shadow.focusRing
  },
  active: {
    backgroundColor: primitiveTokens.colors.purple[400],
    borderColor: primitiveTokens.colors.purple[200]
  },
  disabled: {
    backgroundColor: primitiveTokens.colors.neutral[50],
    borderColor: primitiveTokens.colors.neutral[200]
  },
  checkmark: {
    backgroundColor: primitiveTokens.colors.purple[700],
    borderColor: primitiveTokens.colors.purple[200],
    hover: {
      borderColor: primitiveTokens.colors.purple[300]
    },
    active: {
      borderColor: primitiveTokens.colors.purple[500]
    },
    disabled: {
      backgroundColor: primitiveTokens.colors.neutral[700],
      borderColor: primitiveTokens.colors.neutral[400]
    }
  },
  label: {
    textColor: primitiveTokens.colors.purple[50],
    invalid: {
      textColor: primitiveTokens.colors.red[500]
    },
    disabled: {
      textColor: primitiveTokens.colors.neutral[400]
    }
  }
};

const select = {
  default: {
    textColor: primitiveTokens.colors.neutral[400],
    textColorSelected: primitiveTokens.colors.white[1000],
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[900], 0.48),
    borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16)
  },
  focus: {
    backgroundColor: primitiveTokens.colors.purple[800]
  },
  hover: {
    backgroundColor: primitiveTokens.colors.purple[800]
  },
  disabled: {
    backgroundColor: primitiveTokens.colors.neutral[800],
    borderColor: primitiveTokens.colors.neutral[700],
    textColorSelected: primitiveTokens.colors.neutral[500],
    textColor: primitiveTokens.colors.neutral[600]
  },
  menuList: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[900], 0.48),
    listItem: {
      textColor: primitiveTokens.colors.purple[50],
      hover: {
        backgroundColor: primitiveTokens.colors.purple[800]
      },
      selected: {
        backgroundColor: primitiveTokens.colors.purple[700]
      }
    }
  },
  icon: {
    color: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.48),
    disabledColor: primitiveTokens.colors.neutral[600]
  }
};

const sheet = {
  transitionDuration: primitiveTokens.transitionDuration.default,
  shadow: primitiveTokens.colors.shadow.sheet,
  layer1: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.04)
  },
  layer2: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.12)
  },
  layer3: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16)
  }
};

const switchStyle = {
  label: {
    textColor: primitiveTokens.colors.purple[50],
    textcolorDisabled: primitiveTokens.colors.neutral[500]
  },
  toggle: {
    indicatorColor: primitiveTokens.colors.purple[800],
    indicatorColorDisabled: primitiveTokens.colors.neutral[500]
  },
  backgroundColor: {
    default: {
      toggled: primitiveTokens.colors.purple[200],
      untoggled: primitiveTokens.colors.neutral[50]
    },
    hover: {
      toggled: primitiveTokens.colors.purple[300],
      untoggled: primitiveTokens.colors.purple[100]
    },
    disabled: {
      toggled: primitiveTokens.colors.neutral[300],
      untoggled: primitiveTokens.colors.neutral[300]
    }
  },
  thumb: {
    backgroundColor: primitiveTokens.colors.purple[800]
  }
};

const tabs = {
  trigger: {
    backgroundColor: "transparent",
    transitionDuration: primitiveTokens.transitionDuration.default,
    transitionProperty: "background-color, color, border-color",
    borderColor: "transparent",
    textColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.92),
    hover: {
      textColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.8),
      backgroundColor: primitiveTokens.colors.purple[700]
    },
    selected: {
      borderColor: primitiveTokens.colors.purple[400],
      backgroundColor: primitiveTokens.colors.purple[500],
      textColor: primitiveTokens.colors.white[1000]
    },
    disabled: {
      textColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36),
      borderColor: primitiveTokens.colors.purple[700],
      backgroundColor: primitiveTokens.colors.purple[800],
      selected: {
        textColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36),
        backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.04)
      }
    },
    focus: {
      boxShadow: primitiveTokens.colors.shadow.focusRing,
      outline: "none"
    }
  },
  separator: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16)
  },
  list: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.12),
    color: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.04)
  }
};

const toggleGroup = {
  item: {
    borderColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.24),
    hover: { backgroundColor: primitiveTokens.colors.purple[700] },
    selected: {
      backgroundColor: primitiveTokens.colors.purple[500],
      borderColor: primitiveTokens.colors.purple[400],
      textColor: primitiveTokens.colors.white[1000]
    },
    focus: {
      boxShadow: primitiveTokens.colors.shadow.focusRing,
      outline: "none"
    }
  }
};

const themeSwitcher = {
  backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.16),
  textColor: primitiveTokens.colors.white[1000],
  iconColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.64),
  thumbColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.36)
};

const transition = primitiveTokens.animations.elementFade;

const userMenu = {
  iconColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.64)
};
const tabsNav = {
  fontSize: primitiveTokens.type.sm,
  default: {
    backgroundColor: "transparent",
    textColor: hslaWithAlpha(primitiveTokens.colors.white[1000], 0.64)
  },
  active: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.12),
    textColor: primitiveTokens.colors.white[1000]
  },
  hover: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.16),
    textColor: primitiveTokens.colors.white[1000]
  }
};

const sparklineGraph = {
  trend: {
    stroke: primitiveTokens.colors.blue[500]
  },
  seasonality: {
    stroke: primitiveTokens.colors.green[400]
  },
  styleSeries: {
    color: primitiveTokens.colors.graph.neutral[400],
    fill: hslaWithAlpha(primitiveTokens.colors.graph.neutral[400], 0.7)
  },
  rosFwd16: {
    negativeMarker: primitiveTokens.colors.red[500]
  },
  rebuyOverstock: {
    full: primitiveTokens.colors.red[500],
    partial: primitiveTokens.colors.graph.yellow[400]
  },
  // The light ramp inverted, not reused: on the dark table surface the run has
  // to climb into the light to stay legible, and step 1 still has to clear the
  // background it sits on.
  orderFlow: {
    1: "hsla(253, 26%, 38%, 1)",
    2: "hsla(253, 36%, 49%, 1)",
    3: "hsla(253, 50%, 60%, 1)",
    4: "hsla(253, 72%, 71%, 1)",
    5: "hsla(253, 92%, 81%, 1)",
    6: "hsla(253, 100%, 91%, 1)"
  }
};

const barChart = {
  salesFill: primitiveTokens.colors.graph.blue[300],
  purchaseFill: primitiveTokens.colors.graph.yellow[500],
  sellthroughFill: primitiveTokens.colors.graph.green[500]
};

const sellthroughChart = {
  color1: primitiveTokens.colors.graph.blue[300],
  color2: primitiveTokens.colors.graph.blue[400],
  color3: primitiveTokens.colors.graph.purple[400],
  color4: primitiveTokens.colors.graph.yellow[400],
  color5: primitiveTokens.colors.graph.red[400]
};

const chart = {
  borderColour: hslaWithAlpha(primitiveTokens.colors.purple[100], 0.16),
  backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[900], 0.48),
  textColor: primitiveTokens.colors.white[1000],
  tooltipBackground: primitiveTokens.colors.neutral[800],
  tooltipTextColor: primitiveTokens.colors.white[1000],
  foregroundColor: "rgba(255, 255, 255, 1)",
  chromeBackgroundColor: primitiveTokens.colors.neutral[900],
  chromeSubtleTextColor: primitiveTokens.colors.neutral[500],
  crosshairLabelBackgroundColor: primitiveTokens.colors.neutral[400],
  crosshairLabelTextColor: primitiveTokens.colors.white[1000],
  gridColor: primitiveTokens.colors.neutral[200],
  palette: {
    fill: {
      1: primitiveTokens.colors.blue[400],
      2: primitiveTokens.colors.purple[300],
      3: primitiveTokens.colors.red[400],
      4: primitiveTokens.colors.blue[500],
      5: primitiveTokens.colors.purple[400],
      6: primitiveTokens.colors.red[500],
      7: primitiveTokens.colors.neutral[400],
      8: primitiveTokens.colors.blue[300],
      9: primitiveTokens.colors.purple[200],
      10: primitiveTokens.colors.red[300]
    },
    stroke: {
      1: primitiveTokens.colors.white[1000],
      2: primitiveTokens.colors.purple[500],
      3: primitiveTokens.colors.red[600],
      4: primitiveTokens.colors.blue[700],
      5: primitiveTokens.colors.purple[600],
      6: primitiveTokens.colors.red[700],
      7: primitiveTokens.colors.neutral[600],
      8: primitiveTokens.colors.blue[600],
      9: primitiveTokens.colors.purple[500],
      10: primitiveTokens.colors.red[600]
    }
  }
};
const transferMap = {
  shape: {
    fill: primitiveTokens.colors.purple[700],
    stroke: primitiveTokens.colors.purple[900]
  }
};

const avatar = {
  color: primitiveTokens.colors.purple[100],
  backgroundColor: hslaWithAlpha(primitiveTokens.colors.purple[700], 0.64)
};

const note = {
  info: {
    backgroundColor: primitiveTokens.colors.purple[800],
    textColor: primitiveTokens.colors.white[1000]
  },
  warning: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.yellow[500], 0.16),
    textColor: primitiveTokens.colors.yellow[300]
  },
  error: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.red[500], 0.16),
    textColor: primitiveTokens.colors.red[300]
  }
};

const errorDetails = {
  container: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.red[900], 0.16),
    borderColor: hslaWithAlpha(primitiveTokens.colors.red[400], 0.24)
  },
  summary: {
    textColor: primitiveTokens.colors.red[200]
  },
  stack: {
    backgroundColor: hslaWithAlpha(primitiveTokens.colors.neutral[900], 0.8),
    textColor: primitiveTokens.colors.red[100]
  }
};

const groupCard = {
  headerOverlayColor: "hsla(253, 35%, 10%, 0.75)",
  statBorderColor: primitiveTokens.colors.neutral[800],
  statLabelTextColor: primitiveTokens.colors.neutral[500],
  thumbnailBorderColor: primitiveTokens.colors.neutral[600]
};

export const darkTheme = createTheme(semanticThemeTokens, {
  transition,
  color,
  backgroundGradient,
  button,
  checkbox,
  chip,
  note,
  barChart,
  sellthroughChart,
  dataTable,
  divider,
  drawer,
  dropdown,
  input,
  label,
  loginPage,
  logo,
  tile,
  navigationMenu,
  radio,
  select,
  sheet,
  switch: switchStyle,
  tabs,
  toggleGroup,
  themeSwitcher,
  userMenu,
  dialog,
  toast,
  tabsNav,
  sparklineGraph,
  chart,
  transferMap,
  avatar,
  errorDetails,
  groupCard
});
